import { isDesktop } from '@/consts/device';
import { getAppStore, setAppStore } from '@/store';
import { RightPanelTab } from '@/store/navigation/types';
import {
  ensureSufficientCredits,
  openOfferIfInsufficient,
} from '@/features/credits';
import isAuthorized from '@/utils/user/isAuthorized';
import openRegistrationModal from '@/utils/user/openRegistrationModal';
import parsePresetAttributes from '@/utils/file/parsePresetAttributes';
import withRunAttribute from '@/utils/file/withRunAttribute';
import { createUnknownOutputsRun } from '@/utils/generation/createGenerationRun';
import { Screen } from '@/types';

import type {
  ContentFile,
  ContentFileAttributes,
  Preset,
  PreviewMediaType,
} from '@/types';
import type { AnalyticsSettings } from '@/utils/analytics/analyticsSettings';
import getAnalyticsSettingsFromAttributes from '../analytics/getAnalyticsSettingsFromAttributes';

export interface AutoGenerationPreflight {
  placeholderUuid: string;
  startedAt: number;
  settings: AnalyticsSettings;
  /** Already carrying `run:` — what the submit sends and the placeholder holds. */
  attributes?: ContentFileAttributes;
  runId: string;
}

/**
 * Pre-flight for the auto-generation flow. Performs the synchronous setup
 * that must happen before any webhook traffic:
 *  1. flag the run on the global store (also flips `shouldSkipAutoGeneration`
 *     so we don't re-enter from the same session)
 *  2. enforce auth — opens the registration modal when missing
 *  3. enforce credits — opens the offer screen when missing
 *  4. create the loading placeholder ContentFile and prepend it
 *  5. navigate to the My Creations panel (desktop tab swap / mobile screen)
 *  6. snapshot analytics settings
 *
 * Returns `null` when auth or credits fail — caller treats that as "abort the
 * run, no placeholder was created". On success returns the placeholder UUID,
 * the run's start timestamp, and the analytics settings.
 */
const prepareAutoGeneration = async ({
  webhookId,
  selectedPreset,
}: {
  webhookId: string;
  selectedPreset: Preset;
}): Promise<AutoGenerationPreflight | null> => {
  // URL was already stripped by the DeeplinkRouter when projectJsonHandler
  // resolved; nothing to do here. The shouldSkipAutoGeneration flag below
  // guards against re-entry from the same session.
  setAppStore({
    isPrimaryActionLoading: true,
    isGenerating: true,
    resultFile: null,
    shouldSkipAutoGeneration: true,
  });

  // Auto-generation is triggered reactively (useAutoGeneration) only once auth
  // is authorized, so this reads a correct auth state without waiting.
  if (!isAuthorized()) {
    openRegistrationModal();
    return null;
  }

  const creditResult = await ensureSufficientCredits({
    flowId: webhookId,
    presetCreditCount: selectedPreset.presetCreditCount,
  });
  if (!creditResult.sufficient) {
    // Auto-gen runs at boot — no touchpoint-dependent UI overrides yet.
    openOfferIfInsufficient(creditResult, { preferSubscription: false });
    return null;
  }

  const startedAt = Date.now();
  const { attributes: projectAttributes } = getAppStore();

  // A remote project never declares how many files its flow returns — the count
  // first shows up in the poll response, long after the submit that has to carry
  // the id — so the id is minted for every run here. It groups nothing until a
  // second file shares it, and it has to be in the drive attributes to survive a
  // reload, which is why it is decided before the webhook goes out.
  const runId = createUnknownOutputsRun({
    slug: parsePresetAttributes(projectAttributes?.category).slug,
    timestamp: startedAt,
  });
  const attributes = withRunAttribute({
    attributes: projectAttributes,
    runId,
  });

  // Create loading placeholder and navigate to My Creations immediately.
  // `attributes` is set here so it survives every subsequent shallow-merge
  // updateContentFile call without polling/finalize needing to know about it.
  const placeholderUuid = `generating-${startedAt}`;
  const placeholder: ContentFile = {
    uuid: placeholderUuid,
    name: 'Generating...',
    url: '',
    isLoading: true,
    generationStartTime: startedAt,
    presetType: selectedPreset.type as PreviewMediaType | undefined,
    ...(attributes && { attributes }),
  };
  getAppStore().prependContentFile(placeholder);

  if (isDesktop) {
    setAppStore({ rightPanelTab: RightPanelTab.Creations });
  } else {
    setAppStore({ screen: Screen.Files });
  }

  const { id, category } = getAnalyticsSettingsFromAttributes(attributes);
  const settings = {
    category,
    id,
  } as AnalyticsSettings;

  return { placeholderUuid, startedAt, settings, attributes, runId };
};

export default prepareAutoGeneration;
